import {
  ArrayFieldsConfig,
  CategoryFieldConfig,
  NodeType,
} from "@/types/unified-model";
import { FileType, Message } from "@/types/chat";
import { StatusEnum } from "@/types/document";
import { CanvasSettings, TabsType } from "@/types/diagram";
export const PAGE_SIZE = 6;
export const DOCUMENT_NOT_FOUND =
  "No documents found. Please upload files to your project to view them here.";
export const MODEL_LIST = ["gpt-4o-mini"];

export const WELCOME_MESSAGE = `👋 Welcome aboard!
I'm here to guide you through your software development journey with the help of our intelligent agents. Each one specializes in a specific domain to assist you from idea to implementation. Here's what they do:`;

export const FILE_TYPE_MAP: Record<string, FileType["fileType"]> = {
  "application/pdf": "pdf",
  "text/plain": "txt",
  "text/markdown": "txt",
  "text/csv": "txt",
  "application/json": "txt",
  "text/html": "office",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "office",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "office",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "office",
};
export const PROJECT_BUNDLE_EXTENSIONS = [".ndjson.gz"] as const;
export type TPanel =
  // | "DOCUMENT_LIST"
  | "UNIFIED_MODEL"
  | "DIAGRAM_TOOL"
  // | "AGENT_LIST"
  | "GHERKIN_SCRIPT"
  | "MARKDOWN_RENDER"
  | "METADATA_VIEWER"
  | "MEDIA_PREVIEW"
  | null;
export enum FUNCTIONAL_MODEL_KEYS {
  Persona = "persona",
  Tasks = "outcome",
  Scenarios = "scenario",
  Steps = "step",
  Actions = "action",
}

// Map from edit keys to their corresponding property names
export const EDIT_KEY_TO_MATCH_KEY: Record<string, string | string[]> = {
  Persona: FUNCTIONAL_MODEL_KEYS.Persona,
  Tasks: FUNCTIONAL_MODEL_KEYS.Tasks,
  Scenarios: FUNCTIONAL_MODEL_KEYS.Scenarios,
  Steps: FUNCTIONAL_MODEL_KEYS.Steps,
  Actions: FUNCTIONAL_MODEL_KEYS.Actions,
  Architecture: "name",
};

export enum AGENTS {
  CHAT_AGENT = "requirement_agent", // Keep URL path for backward compatibility
  DESIGN_AGENT = "design_agent",
  ARCHITECTURE_AGENT = "architecture_agent",
  // BREEZE_AGENT = "breeze_agent",
  ATDD_AGENT = "atdd_agent",
  // CODE_GENERATION_AGENT = "code_generation_agent",
}

export const AGENTS_VALUES: Record<AGENTS, string> = {
  [AGENTS.CHAT_AGENT]: "AI Chat Assistant",
  [AGENTS.DESIGN_AGENT]: "Design Agent",
  [AGENTS.ARCHITECTURE_AGENT]: "Architecture Agent",
  // [AGENTS.BREEZE_AGENT]: "Visual Agent",
  [AGENTS.ATDD_AGENT]: "ATDD Agent",
  // [AGENTS.CODE_GENERATION_AGENT]: "Code Generation Agent",
};

/**
 * Build the in-app path for a chat screen. CHAT_AGENT (the default agent) is
 * emitted without its slug so the URL stays clean — `/chat/{id}` instead of
 * `/chat/requirement_agent/{id}`. Threaded deep-links for CHAT_AGENT go
 * through `/chat/{id}/t/{threadId}`; the literal `t` disambiguates from the
 * two-segment `/chat/:agent/:id` pattern used by non-default agents. Old URLs
 * with the `requirement_agent` slug still resolve — the historical routes are
 * kept for backward compatibility.
 */
export function buildChatPath(
  agent: AGENTS | string,
  id: string,
  threadId?: string,
): string {
  if (agent === AGENTS.CHAT_AGENT) {
    return threadId ? `/chat/${id}/t/${threadId}` : `/chat/${id}`;
  }
  return threadId
    ? `/chat/${agent}/${id}/${threadId}`
    : `/chat/${agent}/${id}`;
}
export const UNIFIED_MODEL_TABS = [
  { name: "Blueprint", enabled: true, show: false },
  { name: "Functional", enabled: false, show: true },
  { name: "Design", enabled: false, show: true },
  { name: "Architecture", enabled: false, show: true },
  { name: "Code", enabled: true, show: true },
  { name: "History", enabled: true, show: true },
  { name: "History", enabled: true, show: false },
];
export const MESSAGE_SHELL: Message = {
  text: "",
  isUser: false,
  isSystemQuery: true,
  metaData: {},
};

export type AGENT_STATUS = "active" | "inactive";
export type AgentStatus = Record<AGENTS, AGENT_STATUS> | undefined;

export const INITIAL_RESIZE_DIMENSIONS = {
  left: 30,
  right: 40,
};
export const CUSTOM_SCROLLBAR =
  "scroll-container overflow-auto scrollbar-thin scrollbar-thumb-surface-border scrollbar-track-surface-dark scrollbar-thumb-rounded-full";

// Maximum character length allowed for chat queries (DEF-787-01).
// Matches backend validation (ChatValidation.query MaxLength 8000) and
// breezeai-agents MAX_QUERY_LENGTH.
export const MAX_CHAT_QUERY_LENGTH = 8000;

// Maximum length of a chat thread title. Matches the backend rename/create
// validation (chat-thread.validation.ts).
export const MAX_THREAD_TITLE_LENGTH = 100;

export const AGENT_DESCRIPTIONS: Record<AGENTS, string> = {
  [AGENTS.CHAT_AGENT]: `
  🧠 **Hi, I'm your AI Chat Assistant!**
  I'm a general-purpose agent with comprehensive access to your Knowledge Management System (KMS) and knowledge graph. I can help with RAG-powered queries, systematic requirement gathering, and artifact generation.
  
  &nbsp;
  
  **🎯 Core Capabilities:**
  * 💬 **RAG Operations** - Query your uploaded documents with intelligent context retrieval
  * 📊 **Knowledge Graph Access** - Leverage interconnected functional, design & architecture data
  * 📋 **Requirement Gathering** - Systematic collection and analysis of business requirements
  * 📄 **Artifact Generation** - Create structured documentation, specs, and deliverables
  
  &nbsp;
  
  **🚀 What I Can Access:**
  * 📚 **KMS Documents** - All your uploaded PDFs, specs, user stories, and documentation
  * 🕸️ **Knowledge Graph** - Connected ontologies across functional, design, and architecture domains
  * 🧠 **Semantic Model** - Rich relationships between personas, outcomes, scenarios, and components
  * 📊 **Project Metrics** - Generated insights and analytics from your documentation
  
  &nbsp;
  
  **💡 Try these advanced prompts:**
  * *"Analyze all uploaded documents and extract key requirements"*
  * *"Generate user stories based on the functional ontology"*
  * *"Create a technical specification from the architecture knowledge graph"*
  * *"What are the connections between personas and design components?"*
  * *"Help me gather requirements for [specific feature] systematically"*
  
  &nbsp;
  
  I'm your comprehensive AI assistant for all knowledge-based operations - from simple Q&A to complex artifact generation!
  `,
  [AGENTS.DESIGN_AGENT]: `
  🎨 **Hello, I'm the Design/UX Agent!**
  I help you bridge the gap between design and development by converting your UX artifacts into a structured **semantic model**, making your user journeys machine-understandable and ready for downstream use.
  
  &nbsp;
  
  **🎯 Chat Modes Available:**
  * 💬 **General Query** - Ask me anything about your design artifacts and user flows
  * 🎨 **Design Mode** - Systematic processing and analysis of UX artifacts
  
  &nbsp;
  
  **🚀 What I Can Do:**
  * 📥 **Upload user flows, wireframes, or journey maps** - I'll extract meaningful design semantics from them
  * 🔄 **Convert UX artifacts** into structured semantic models for engineering alignment
  * 🧠 **Generate semantic mappings** that connect design elements to functional requirements
  * 📊 **Analyze user interactions** and create machine-readable design specifications
  
  &nbsp;
  
  **💡 Try these prompts:**
  * *"What are the key user interactions in this flow?"* (General Query)
  * *"Process this wireframe and extract user journeys"* (Design Mode)
  * *"List all steps for the checkout persona"* (General Query)
  * *"Generate semantic model from uploaded designs"* (Design Mode)
  
  &nbsp;
  
  Select your preferred chat mode above and let's make your UX speak the same language as the rest of the system!
  `,
  [AGENTS.ARCHITECTURE_AGENT]: `
  🏗️ **Hi, I'm the Architecture Agent!**
  I help you create, visualize, and manage architecture diagrams and ontologies. Upload images or documents, and I'll transform them into interactive, structured models.

  &nbsp;

  **🎯 Core Capabilities:**
  * 🖼️ **Diagram from Images** - Upload architecture diagram images and I'll generate editable, interactive diagrams from them
  * 🧱 **Diagram Editing** - Add layers, shapes, or components through natural chat commands
  * 📚 **Architecture Ontology** - Upload technical documents to generate architecture ontology with components, relationships, and dependencies
  * 🔗 **Functional Mapping** - View automatic connections between architecture components and functional requirements

  &nbsp;

  **💡 Try these prompts:**
  * *"Add a new microservice layer to the diagram"*
  * *"Move the database behind the firewall"*
  * *"Add a load balancer between gateway and services"*
  * *"What components are connected to the API gateway?"*

  &nbsp;

  **📤 Export Options:**
  Export your diagrams in **SVG** or **PNG** format for documentation and sharing.

  &nbsp;

  Use the **+** button to attach files or generate diagrams from your architecture ontology!
  `,
  [AGENTS.ATDD_AGENT]: `
  🧪 **Hi, I'm the ATDD Agent!**
  I'm here to help you adopt **Acceptance Test Driven Development (ATDD)** by bridging requirements and testing through automation.
  
  &nbsp;

  Here's what you can do with me:
  * 📥 **Upload test case artifacts**—I'll analyze and incorporate them into the unified semantic model.
  * ✍️ **Generate Gherkin scripts** from functional requirements using natural prompts like *"Generate Gherkin"* or *"Create test scenarios for user login."*
  * 🔄 **Ensure traceability** between your test scenarios and functional requirements to validate business intent from the start.
  
  &nbsp;

  Let's bring QA and dev closer by making acceptance criteria explicit, automated, and collaboration-ready!
  `,
  // [AGENTS.BREEZE_AGENT]: `
  // 🌬️ **Hi, I'm the Visual Agent!** I help bring your functional requirements to life by mapping them to **Accion's Breeze blueprint** and generating rich visual diagrams.

  // &nbsp;

  // Here's what you can do with me:
  // * 🏗️ **Generate visual diagrams** using Breeze specification with commands like *"Generate Breeze blueprint."*
  // * 🤖 **Ask Breeze-specific queries**, e.g., *"What entities and workflow services are needed for this requirement?"*
  // * 🔍 **Explore functional mappings** for each identified component in the diagram—automatically linked to your uploaded requirements.
  // * ✏️ **Manipulate the diagram** by dragging shapes or using natural commands like *"Move notification service behind API Gateway."*

  // &nbsp;

  // Let's turn business needs into blueprint-backed system views—effortlessly!
  // `,
};

export const INITIAL_FILE_STATE = {
  file: null,
  src: "",
  fileType: null,
};

export const NODE_COLORS: Record<NodeType, string> = {
  category: "#e0e0e0", // light gray
  component: "#d1c4e9", // soft purple
  scenario: "#cce5ff", // light blue
  detail: "#ffe0b2", // soft orange
  persona: "#ffddc1", // peach
  outcome: "#d0f4de", // mint
  step: "#f3d1f4", // pinkish lavender
  action: "#fff4cc", // light yellow
};

export const STATUS_COLORS: Record<StatusEnum, string> = {
  [StatusEnum.none]: "bg-red-500",
  [StatusEnum.inprogress]: "bg-yellow-500",
  [StatusEnum.done]: "bg-green-500",
  [StatusEnum.error]: "bg-red-800 border-2 border-red-400",
};
export const KMS_INDEXING_STATUS: Record<StatusEnum, string> = {
  [StatusEnum.none]: "file is uploaded but not indexed.",
  [StatusEnum.inprogress]: "file indexing is in progress.",
  [StatusEnum.done]: "file has sucessfully indexed.",
  [StatusEnum.error]: "file indexing failed.",
};
export const KMS_MODEL_STATUS: Record<StatusEnum, string> = {
  [StatusEnum.none]: " is not generated.",
  [StatusEnum.inprogress]: " generation is in progress.",
  [StatusEnum.done]: " has sucessfully generated.",
  [StatusEnum.error]: " generation failed.",
};
export const WENT_WRONG = "Something went wrong. Please try again later.";
export const THREAD_BUSY =
  "The previous answer in this conversation is still being generated. Please wait for it to finish, then send your message again.";
export const REFETCH_INTERVAL = 5000;
export const TOAST_DURATION = 3000;

export const ARCHIVE_TAB_KEY = "archived" as const;

// Updated configuration - simplified to use standard fields for all categories
export const CATEGORY_FIELD_CONFIG: CategoryFieldConfig = {
  user_experience: ["name", "description", "group", "scenarios"],
  api_gateway: ["name", "description", "group", "scenarios"],
  observability_monitoring: ["name", "description", "group", "scenarios"],
  agents: ["name", "description", "group", "scenarios"],
  services: ["name", "description", "group", "scenarios"],
  event_queue: ["name", "description", "group", "scenarios"],
  data_lake: ["name", "description", "group", "scenarios"],
  infrastructure: ["name", "description", "group", "scenarios"],
  // Default
  default: ["name", "description", "group", "scenarios"],
};

// Updated array fields - scenarios is array for all categories
export const ARRAY_FIELDS: ArrayFieldsConfig = {
  user_experience: ["scenarios"],
  api_gateway: ["scenarios"],
  observability_monitoring: ["scenarios"],
  agents: ["scenarios"],
  services: ["scenarios"],
  event_queue: ["scenarios"],
  data_lake: ["scenarios"],
  infrastructure: ["scenarios"],
  default: ["scenarios"],
};

// Workflow Guidelines Data
export interface WorkflowStep {
  action: string;
  icon: string; // Icon name as string, will be mapped to actual icons in components
}

export interface WorkflowDefinition {
  id: string;
  title: string;
  description: string;
  icon: string; // Icon name as string
  status: "active" | "inactive" | "processing";
  steps: WorkflowStep[];
  navigation: {
    type: "navigate" | "panel" | "external";
    target: string;
    params?: Record<string, any>;
  };
}

export const WORKFLOW_DEFINITIONS: WorkflowDefinition[] = [
  {
    id: "design-mockups",
    title: "Generate Design Mockups",
    description:
      "Transform requirements into interactive HTML mockups with AI assistance",
    icon: "Smartphone",
    status: "active",
    steps: [
      { action: "Upload requirement documents", icon: "Upload" },
      { action: "Generate functional metrics", icon: "Settings" },
      { action: "Chat with Design Agent", icon: "Palette" },
      { action: "Create screen mockups", icon: "Code2" },
    ],
    navigation: { type: "navigate", target: "/chat/design_agent/{id}" },
  },
  {
    id: "breeze-cli-bdd",
    title: "Breeze CLI - BDD Code Generation",
    description:
      "Advanced CLI tool for frontend/backend code generation using BDD approach with unified model integration",
    icon: "Terminal",
    status: "active",
    steps: [
      { action: "Setup unified model integration", icon: "Layers2" },
      { action: "Generate UI/API code with BDD", icon: "Code2" },
      { action: "Reuse existing components & structure", icon: "RefreshCw" },
      { action: "Generate tests & integration", icon: "Target" },
    ],
    navigation: {
      type: "external",
      target: "https://github.com/accionlabs/breeze-cli",
    },
  },
  {
    id: "knowledge-management",
    title: "Unified Knowledge Base",
    description:
      "Centralized document management with AI-powered Q&A and insights",
    icon: "Brain",
    status: "active",
    steps: [
      { action: "Upload multiple documents", icon: "Upload" },
      { action: "Generate unified metrics", icon: "Settings" },
      { action: "Chat with AI agents for insights", icon: "MessageSquare" },
      { action: "Export knowledge artifacts", icon: "Eye" },
    ],
    navigation: { type: "navigate", target: "/knowledge/{id}" },
  },
  {
    id: "architecture-overview",
    title: "Architecture Visualization",
    description:
      "Create comprehensive system architecture diagrams and documentation",
    icon: "GitBranch",
    status: "active",
    steps: [
      { action: "Upload technical documents", icon: "Upload" },
      { action: "Generate architecture metrics", icon: "Settings" },
      { action: "Chat with Architecture Agent", icon: "GitBranch" },
      { action: "Generate visual diagrams", icon: "Code2" },
    ],
    navigation: { type: "navigate", target: "/chat/architecture_agent/{id}" },
  },
  {
    id: "reverse-engineering",
    title: "Reverse Engineering",
    description:
      "Reverse engineer existing code repositories into structured documentation and metrics using Breeze CLI",
    icon: "Search",
    status: "active",
    steps: [
      { action: "Upload code repository", icon: "FolderOpen" },
      { action: "Run Breeze CLI analysis", icon: "Terminal" },
      { action: "Generate documentation", icon: "FileText" },
      {
        action: "Extract functional, design & architecture metrics",
        icon: "BarChart3",
      },
    ],
    navigation: { type: "navigate", target: "/knowledge/{id}" },
  },
  {
    id: "ui-code-generation",
    title: "Design to UI Code",
    description:
      "Convert design screens into production-ready UI code for any framework using Breeze CLI",
    icon: "Code2",
    status: "active",
    steps: [
      { action: "Go to Design Agent workspace", icon: "Palette" },
      { action: "Select target design screen", icon: "Eye" },
      { action: "Choose target framework", icon: "Settings" },
      { action: "Generate code using Breeze CLI", icon: "Terminal" },
    ],
    navigation: { type: "navigate", target: "/chat/design_agent/{id}" },
  },
  {
    id: "artifacts-generation",
    title: "Generate Project Artifacts",
    description:
      "Create comprehensive documentation and artifacts using functional analysis",
    icon: "FileX",
    status: "active",
    steps: [
      { action: "Upload project documents", icon: "Upload" },
      { action: "Generate functional metrics", icon: "Settings" },
      { action: "Use Functional Agent for artifacts", icon: "Brain" },
      { action: "Export documentation artifacts", icon: "Download" },
    ],
    navigation: { type: "navigate", target: "/knowledge/{id}?tab=artifacts" },
  },
  {
    id: "unified-model-export",
    title: "Export & Reuse Unified Model",
    description:
      "Export unified semantic model and use it as input for other AI agents",
    icon: "Layers2",
    status: "active",
    steps: [
      { action: "View unified semantic model", icon: "Eye" },
      { action: "Export model data", icon: "Download" },
      { action: "Feed to other AI agents", icon: "RefreshCw" },
      { action: "Generate new insights", icon: "Brain" },
    ],
    navigation: { type: "panel", target: "UNIFIED_MODEL" },
  },
  {
    id: "unified-model-customization",
    title: "Customize Unified Model",
    description:
      "Update and enhance unified model with custom personas and requirements",
    icon: "Edit3",
    status: "active",
    steps: [
      { action: "Open unified model viewer", icon: "Eye" },
      { action: "Add custom personas/actions", icon: "Edit3" },
      { action: "Chat with Functional Agent", icon: "Brain" },
      { action: "Update model with new requirements", icon: "RefreshCw" },
    ],
    navigation: { type: "panel", target: "UNIFIED_MODEL" },
  },
];

export const DEFAULT_SETTINGS: CanvasSettings = {
  canvas: {
    canvasSize: {
      width: 1000,
      height: 1000,
    },
    showAttachmentPoints: false,
  },
  metadataLabel: {
    minSpacing: 200,
    minYSpacing: 17,
    smoothingAngle: (120 * Math.PI) / 180, // 120 degrees in radians
    stepSize: 20,
  },
  layerLabel: {
    width: 200,
    lineSpacing: 1.2,
    fontFamily: "sans-serif",
    fontSize: 60,
    fontWeight: "bold",
    hideLabels: false,
  },
};

export const panels: Array<{ id: TabsType; label: string }> = [
  { id: "shapes", label: "Shapes" },
  { id: "composition", label: "Composition" },
  { id: "chat", label: "AI Agent" },
  { id: "diagrams", label: "Diagrams" },
];

export const MENU_ITEMS = [
  {
    group: "main",
    items: [
      { label: "New Diagram", action: "new-diagram" },
      { label: "Load Diagram", action: "load-diagram" },
      { label: "Save Diagram", action: "save-diagram" },
      { label: "Save as Component", action: "save-component" },
    ],
  },
  {
    group: "export",
    items: [
      { label: "Download SVG", action: "download-svg" },
      { label: "Download PNG", action: "download-png" },
      { label: "Download Diagram", action: "download-diagram" },
    ],
  },
  {
    group: "settings",
    items: [{ label: "Display Settings", action: "display-settings" }],
  },
];
